import React from 'react';
import { Mic, MicOff, Plane, Clock, Volume2, Target, Lightbulb, Type, Eye, EyeOff } from 'lucide-react';
import Card from '../../../components/Card/Card';
import SectionHeader from '../../../components/SectionHeader/SectionHeader';

function DictaphoneTab() {
  return (
    <>
      {/* Section: Enregistrement */}
      <Card className="card-large">
        <SectionHeader icon={Mic} title="Enregistrer une dictée" variant="default" />
        <div className="guide-section-content">
          <div className="guide-steps">
            <div className="guide-step">
              <span className="guide-step-number">1</span>
              <div className="guide-step-content">
                <h4>Appuyez sur le bouton microphone</h4>
                <p>Le bouton rouge au centre de l'écran lance l'enregistrement.</p>
              </div>
            </div>
            <div className="guide-step">
              <span className="guide-step-number">2</span>
              <div className="guide-step-content">
                <h4>Parlez normalement</h4>
                <p>L'application attend que vous parliez. Dictez votre rapport comme d'habitude.</p>
              </div>
            </div>
            <div className="guide-step">
              <span className="guide-step-number">3</span>
              <div className="guide-step-content">
                <h4>Appuyez à nouveau pour terminer</h4>
                <p>Le texte est automatiquement transcrit et envoyé pour générer votre rapport.</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Section: Ponctuation */}
      <Card className="card-large">
        <SectionHeader icon={Type} title="Ponctuation vocale" variant="info" />
        <div className="guide-section-content">
          <div className="guide-info">
            <Type className="guide-info-icon" size={20} />
            <p>
              Vous pouvez <strong>dicter la ponctuation</strong> pour guider l'IA :
              "point", "virgule", "à la ligne", "nouveau paragraphe"...
              Mais ce n'est <strong>pas obligatoire</strong> : l'IA sait placer la ponctuation automatiquement.
            </p>
          </div>
          <div className="guide-tip">
            <Lightbulb className="guide-tip-icon" size={20} />
            <p>
              Utilisez la ponctuation vocale surtout pour structurer votre texte
              (nouveaux paragraphes, listes) ou pour des mises en forme spécifiques.
            </p>
          </div>
        </div>
      </Card>

      {/* Section: Transcription provisoire */}
      <Card className="card-large">
        <SectionHeader icon={EyeOff} title="Transcription provisoire" variant="warning" />
        <div className="guide-section-content">
          <div className="guide-warning">
            <EyeOff className="guide-warning-icon" size={20} />
            <p>
              Le texte qui s'affiche pendant que vous dictez est une <strong>transcription provisoire</strong>.
              Il est normal qu'il contienne des fautes ou des approximations.
            </p>
          </div>
          <div className="guide-tip">
            <Lightbulb className="guide-tip-icon" size={20} />
            <p>
              <strong>Ne vous arrêtez pas pour lire ce texte.</strong> Il sert uniquement à vous repérer
              dans votre dictée. Le rapport final sera généré par l'IA avec une bien meilleure qualité.
            </p>
          </div>
        </div>
      </Card>

      {/* Section: Détection vocale */}
      <Card className="card-large">
        <SectionHeader icon={Volume2} title="Pause automatique intelligente" variant="success" />
        <div className="guide-section-content">
          <div className="guide-info">
            <Target className="guide-info-icon" size={20} />
            <p>
              L'application détecte automatiquement quand vous parlez.
              Après <strong>2 secondes de silence</strong>, elle se met en pause.
              Reprenez simplement la parole pour continuer.
            </p>
          </div>
          <div className="guide-features">
            <div className="guide-feature">
              <div className="guide-feature-icon primary">
                <Clock size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>Économie de batterie</h4>
                <p>N'enregistre que quand vous parlez</p>
              </div>
            </div>
            <div className="guide-feature">
              <div className="guide-feature-icon green">
                <Volume2 size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>Meilleure qualité</h4>
                <p>Évite d'enregistrer les bruits de fond</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Section: Mode Avion */}
      <Card className="card-large">
        <SectionHeader icon={Plane} title="Mode avion (hors connexion)" variant="default" />
        <div className="guide-section-content">
          <div className="guide-tip">
            <Plane className="guide-tip-icon" size={20} />
            <p>
              Pas de connexion internet ? Pas de problème !
              Le mode avion vous permet de continuer à dicter. Vos enregistrements seront
              automatiquement synchronisés dès que vous retrouverez du réseau.
            </p>
          </div>
          <div className="guide-features">
            <div className="guide-feature">
              <div className="guide-feature-icon orange">
                <Plane size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>Enregistrement local</h4>
                <p>Vos dictées sont stockées sur votre appareil</p>
              </div>
            </div>
            <div className="guide-feature">
              <div className="guide-feature-icon blue">
                <MicOff size={20} />
              </div>
              <div className="guide-feature-text">
                <h4>Sync automatique</h4>
                <p>Transcription dès le retour de la connexion</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Section: Conseils */}
      <Card className="card-large">
        <SectionHeader icon={Mic} title="Conseils pour une meilleure transcription" variant="info" />
        <div className="guide-section-content">
          <div className="guide-steps">
            <div className="guide-step">
              <span className="guide-step-number">1</span>
              <div className="guide-step-content">
                <h4>Distance optimale</h4>
                <p>Tenez votre téléphone à 10-20 cm de votre bouche.</p>
              </div>
            </div>
            <div className="guide-step">
              <span className="guide-step-number">2</span>
              <div className="guide-step-content">
                <h4>Environnement calme</h4>
                <p>Évitez les endroits bruyants pour une meilleure qualité.</p>
              </div>
            </div>
            <div className="guide-step">
              <span className="guide-step-number">3</span>
              <div className="guide-step-content">
                <h4>Évitez les écouteurs Bluetooth</h4>
                <p>Le micro intégré de votre appareil donne de meilleurs résultats.</p>
              </div>
            </div>
            <div className="guide-step">
              <span className="guide-step-number">4</span>
              <div className="guide-step-content">
                <h4>Choisissez le bon modèle</h4>
                <p>Sélectionnez le type de rapport avant de dicter pour une meilleure précision.</p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </>
  );
}

export default DictaphoneTab;
